import React from 'react';
import { motion } from 'framer-motion';

const Projects = () => {
  // Animation variants - EXACTLY like Skills section
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  // Function to render description with bold technology names and company names
  const renderDescription = (description) => {
    // First, handle existing <strong> tags for technologies
    let processedDescription = description;
    
    // Make company names bold and styled
    const companyNames = [
      'Eloelo', 'eloelo', 'EloElo',
      'Hood', 'hood',
      'Primathon Ventures', 'primathon ventures', 'Primathon',
      'EasyEat', 'easyeat', 'EasyEat',
      'AudioVersity', 'audioversity'
    ];
    
    // Replace company names with unique placeholders first
    companyNames.forEach((companyName, index) => {
      const placeholder = `__COMPANY_${index}__`;
      const regex = new RegExp(`\\b${companyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
      processedDescription = processedDescription.replace(regex, placeholder);
    });
    
    // Split by <strong> tags first
    const strongParts = processedDescription.split(/(<strong>.*?<\/strong>)/);
    
    // Process each part
    return strongParts.map((part, index) => {
      if (part.startsWith('<strong>') && part.endsWith('</strong>')) {
        const text = part.replace(/<\/?strong>/g, '');
        return <strong key={index}>{text}</strong>;
      }
      
      // Replace company placeholders with styled spans
      let processedPart = part;
      companyNames.forEach((companyName, companyIndex) => {
        const placeholder = `__COMPANY_${companyIndex}__`;
        if (processedPart.includes(placeholder)) {
          processedPart = processedPart.replace(placeholder, 
            `<span className="company-name-highlight">${companyName}</span>`
          );
        }
      });
      
      // If the part contains company spans, split and render them
      if (processedPart.includes('className="company-name-highlight"')) {
        const companyParts = processedPart.split(/(<span className="company-name-highlight">.*?<\/span>)/);
        return companyParts.map((companyPart, companyPartIndex) => {
          if (companyPart.startsWith('<span className="company-name-highlight"') && companyPart.endsWith('</span>')) {
            const text = companyPart.replace(/<span className="company-name-highlight">(.*?)<\/span>/, '$1');
            return <span key={`${index}-${companyPartIndex}`} className="company-name-highlight">{text}</span>;
          }
          return companyPart;
        });
      }
      
      return processedPart;
    });
  };

  const projects = [
    {
      id: 1,
      company: "Greenhorn Wellness Pvt Ltd (Eloelo)",
      companyLogo: "/images/logos/eloelo-logo.png",
      title: "Creator-led Social Gaming & Live Streaming Platform",
      description: "At Eloelo, I helped build and scale a live streaming and social gaming platform that serves millions of users. One of my key contributions was architecting the host–cohost call system, where a host can go live and seamlessly bring in cohosts or users to join the session. For real-time video and audio streaming, I used <strong>Agora</strong>, and to power live events like call requests, approvals, reactions, and engagement signals, I deployed <strong>EMQX (MQTT broker)</strong> on <strong>AWS EKS</strong>, which now handles millions of concurrent users and events reliably.",

      techSummary: "AWS EKS, EMQX, WebSocket, Lambda, Elastic Cache, MongoDB, Kafka, Kubernetes, ArgoCD, CI/CD, ECS, API Gateway, Agora",
      website: "https://eloelo.in/",
      instagram: "https://www.instagram.com/eloelo_app?igsh=MXNvZ3pyYnpoc3BheQ==",
      linkedin: "https://www.linkedin.com/company/eloeloapp"
    },
    {
      id: 2,
      company: "Hood Internet Pvt Ltd",
      companyLogo: "/images/logos/hood-logo.png",
      title: "Pseudonymous Social Network | Featured in Shark Tank India 2",
      description: "At Hood, I scaled a next-generation social network that managed 150K+ requests per second, ensuring smooth user engagement during peak Shark Tank India coverage. I developed a personalized feed algorithm, built real-time timelines with <strong>Redis</strong> and <strong>DynamoDB</strong>, and implemented advanced relationship analysis using a <strong>Graph Database</strong>. I also architected a highly available infrastructure with <strong>EC2</strong>, <strong>RDS</strong>, <strong>DynamoDB</strong>, <strong>Elasticsearch</strong>, <strong>RabbitMQ</strong>, and <strong>Kubernetes</strong> autoscaling, ensuring reliability, performance, and seamless feature rollouts.",

      techSummary: "AWS EC2, RDS, DynamoDB, Elasticsearch, Neptune, ElastiCache, RabbitMQ, Kubernetes, Redis",
      website: "https://www.hood.live/",
      instagram: "https://www.instagram.com/joinhoodapp?igsh=bnBzeXFoZXl6Y25y",
      linkedin: "https://www.linkedin.com/company/joinhoodapp"
    },
    {
      id: 3,
      company: "Primathon Ventures Pvt Ltd",
      companyLogo: "/images/logos/primathon-logo.png",
      title: "Tech Consultancy & Product Development",
      description: "At Primathon Ventures, I led the backend development of high-impact client projects across multiple industries. I built a real-time job portal with chat and recommendation systems, contributed to RIVIGO's Relay as a Service (RAAS) logistics project, and developed a customer review app with <strong>Node.js</strong> and <strong>React.js</strong>. I also integrated <strong>AWS Textract</strong> for intelligent document processing, enabling automation and efficiency.",

      techSummary: "Node.js, MongoDB, WebSockets, FastAPI, React.js, AWS Textract",
      website: "https://primathon.in",
      instagram: "https://www.instagram.com/primathon_tech?igsh=bzhzMjZqOXdiNGUz",
      linkedin: "https://www.linkedin.com/company/primathon"
    },
    {
      id: 4,
      company: "Easyeat Application",
      companyLogo: "/images/logos/easyeat-logo.png",
      title: "Contactless Restaurant Ordering App",
      description: "At EasyEat, I developed a contactless <strong>Android</strong> app that digitized restaurant ordering and payments in Malaysia. The solution improved customer experience, hygiene, and operational efficiency, particularly during the COVID-19 pandemic. I implemented real-time order tracking with <strong>MQTT</strong> sockets, integrated backend APIs with the <strong>Volley</strong> library, and ensured a smooth end-to-end digital experience for diners and restaurants.",

      techSummary: "Android, Java, Volley, MQTT, MySQL",
      website: "https://get.easyeat.ai",
      instagram: "https://www.instagram.com/easyeat.ai?igsh=MWt4dXU0bnpzODl6cQ==",
      linkedin: "https://www.linkedin.com/company/easyeatai"
    },
    {
      id: 5,
      company: "Audioversity Application",
      companyLogo: "music-icon",
      title: "Music Discovery Platform",
      description: "At AudioVersity, I designed and delivered a global music and podcast discovery platform that connected artists with listeners. I built backend APIs with <strong>NestJS</strong>, integrated the <strong>Spotify Web API</strong> for music discovery, and deployed the platform on <strong>AWS EC2</strong> with <strong>Nginx</strong> and <strong>Route53</strong> for secure and reliable access. The platform provided a seamless streaming experience while enabling new opportunities for artists to reach audiences.",

      techSummary: "NestJS, Spotify Web API, AWS EC2, Nginx, Route 53",
      website: null,
      instagram: null,
      linkedin: null
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="projects" className="projects-section">
      <div className="container">
        {/* Section Header */}
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">Enterprise Solutions Delivered</h2>
          <p className="section-subtitle">
            Leading end-to-end development and architecture for scalable business solutions across industries
          </p>
        </motion.div>

        {/* Projects Grid */}
        <motion.div 
          className="projects-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              className="project-card"
              variants={itemVariants}
            >
              {/* Project Header */}
              <div className="project-header">
                <motion.div 
                  className="company-logo-container"
                  whileHover={{ 
                    scale: 1.1,
                    rotate: 5,
                    transition: { duration: 0.3 }
                  }}
                  whileTap={{ 
                    scale: 0.95,
                    transition: { duration: 0.1 }
                  }}
                >
                  {project.companyLogo === "music-icon" ? (
                    <motion.div 
                      className="music-icon"
                      whileHover={{ 
                        scale: 1.2,
                        rotate: 10,
                        transition: { duration: 0.3 }
                      }}
                    >
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                      </svg>
                    </motion.div>
                  ) : (
                    <motion.img 
                      src={project.companyLogo} 
                      alt={`${project.company} Logo`}
                      className="company-logo"
                      whileHover={{ 
                        scale: 1.2,
                        rotate: 5,
                        transition: { duration: 0.3 }
                      }}
                    />
                  )}
                </motion.div>
                <div className="project-info">
                  <h3 className="project-company">{project.company}</h3>
                  <h4 className="project-title">{project.title}</h4>
                </div>
              </div>

              {/* Company Links - Only show when there are links */}
              {(project.website || project.instagram || project.linkedin) && (
                <div className="company-links">
                  {project.website && (
                    <motion.a 
                      href={project.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="company-link website"
                      title="Visit Company Website"
                      whileHover={{ 
                        scale: 1.2,
                        y: -2,
                        transition: { duration: 0.2 }
                      }}
                      whileTap={{ 
                        scale: 0.9,
                        transition: { duration: 0.1 }
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                      </svg>
                    </motion.a>
                  )}
                  {project.instagram && (
                    <motion.a 
                      href={project.instagram} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="company-link instagram"
                      title="Follow on Instagram"
                      whileHover={{ 
                        scale: 1.2,
                        y: -2,
                        transition: { duration: 0.2 }
                      }}
                      whileTap={{ 
                        scale: 0.9,
                        transition: { duration: 0.1 }
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </motion.a>
                  )}
                  {project.linkedin && (
                    <motion.a 
                      href={project.linkedin} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="company-link linkedin"
                      title="View Company Profile"
                      whileHover={{ 
                        scale: 1.2,
                        y: -2,
                        transition: { duration: 0.2 }
                      }}
                      whileTap={{ 
                        scale: 0.9,
                        transition: { duration: 0.1 }
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </motion.a>
                  )}
                </div>
              )}

              {/* Project Description */}
              <p className="project-description">{renderDescription(project.description)}</p>



              {/* Technologies Used */}
              <div className="project-technologies">
                <div className="tech-tags">
                  {project.techSummary.split(', ').map((tech, idx) => (
                    <span key={idx} className="tech-tag">{tech}</span>
                  ))}
                </div>
              </div>


            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        {/* <motion.div 
          className="projects-cta"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <h3>Ready to Scale Your Business?</h3>
          <p>Let's discuss how we can build scalable solutions that drive your business growth and success.</p>
          <button 
            className="cta-button primary"
            onClick={() => scrollToSection('contact')}
          >
            Start Your Project
          </button>
        </motion.div> */}
      </div>
    </section>
  );
};

export default Projects; 